import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Is depression at 80 just a normal part of aging?",
    answer:
      "No. Depression is not a normal part of aging. Most people in their eighties are not depressed. The heaviness, the loss of interest, the sleeplessness, the fatigue, the trouble remembering, and the pains that never fully resolve are the symptoms of a medical illness — not the natural cost of being 80. Untreated depression at this age worsens every other health problem, speeds physical decline, and takes years off both the length and quality of life. Treating it is not a luxury. It's medicine.",
  },
  {
    question: "Could my memory problems be depression instead of dementia?",
    answer:
      "Very possibly. Depression is one of the most common causes of memory and concentration problems in older adults, and when it's treated, the thinking often improves. It does not mean dementia. A proper evaluation can distinguish between the two, and treating the depression may resolve the cognitive symptoms entirely.",
  },
  {
    question: "Is it safe to treat depression at 80?",
    answer:
      "Yes. Therapy works at 80 — older adults engage well with it and benefit. Antidepressants can help, chosen with great care because of interactions and heightened sensitivity to side effects. And SAINT, a non-drug, FDA-cleared brain stimulation treatment, involves no anesthesia, no sedation, no drug interactions, and takes five days. It's often the safest option for someone whose medication list is already long. The consultation includes a careful health screening to determine eligibility.",
  },
  {
    question: "What if my parent or grandparent is 80 and depressed?",
    answer:
      "The most important thing you can do is refuse to let it be dismissed as age. Get them to the appointments. Sit in on them if they'll have you. Ask about non-drug options if medication isn't working or isn't safe. Remember that treating their depression is not just about mood — it affects their heart, their memory, their mobility, and how many good years they have left. Family members are welcome at every step.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "Diagnosed With Depression at 70: You Have Every Right to Feel Better",
    href: "/diagnosed-with-depression-at-70",
    description:
      "Why depression at 70 is not a normal part of aging — and what the years ahead can hold.",
  },
  {
    title:
      "Depression in the Second Half of Life: What Happens Next Is Up to You",
    href: "/depression-in-the-second-half-of-life",
    description:
      "Why the second half of life is not a reason to settle — and what treatment looks like now.",
  },
  {
    title: 'Diagnosed With Depression at 60: This Is Not "Just Getting Older"',
    href: "/diagnosed-with-depression-at-60",
    description:
      "Why depression at 60 is the most misdiagnosed and undertreated it will ever be.",
  },
  {
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT® against medication, standard TMS, esketamine, and ECT.",
  },
];

export default function DiagnosedDepressionAt80() {
  return (
    <DepressionContentPage
      seo={{
        title: "Diagnosed With Depression at 80? It Is Not Too Late",
        description:
          "Depression at 80 is not just age. What the diagnosis means, why it shows up, what treatment looks like, and a gentle non-drug option with no anesthesia and no drug interactions — in five days.",
        canonical: "/diagnosed-with-depression-at-80",
        keywords:
          "diagnosed with depression at 80, major depressive disorder eighties, treatment-resistant depression elderly, SAINT TMS, depression not aging, memory problems depression, non-drug depression treatment, fMRI-guided TMS, depression octogenarian, gentle depression treatment",
      }}
      badge="For Adults"
      heroTitle="Diagnosed With Depression at 80: It Is Not Too Late, and It Is Not Just Age"
      heroSubtitle="If you're 80 and a doctor has told you that you have major depressive disorder, you may be the first person in your family to hear that phrase said out loud. Your generation didn't talk about depression. Now, at an age when most people expect you to slow down and endure, someone has named the thing and suggested treating it. Please take them up on it."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                If you're 80 and a doctor has told you that you have major
                depressive disorder, you may be the first person in your family
                to hear that phrase said out loud. Your generation didn't talk
                about depression. It was managed with silence, with work, with
                faith, with a stiff upper lip. And now, at an age when most
                people expect you to slow down and endure, someone has named the
                thing and suggested treating it.
              </p>
              <p>Please take them up on it. Here's why.</p>
            </div>
          ),
        },
        {
          heading: "What the diagnosis means",
          body: (
            <div className="space-y-5">
              <p>
                It means that the heaviness, the loss of interest, the
                sleeplessness, the fatigue, the trouble remembering, and the
                pains that never fully resolve are the symptoms of a medical
                illness. Not the natural cost of being 80. Not a failure of
                will. An illness, with treatments, that people your age respond
                to.
              </p>
              <p>
                Depression is not a normal part of aging. Most people in their
                eighties are not depressed. The ones who are deserve to be
                treated as seriously as they'd be treated for a heart condition,
                because untreated depression at this age worsens every other
                health problem, speeds physical decline, and takes years off
                both the length and the quality of life.
              </p>
              <p>
                It does not mean dementia. Depression is one of the most common
                causes of memory and concentration problems in older adults, and
                when it's treated, the thinking often improves.
              </p>
            </div>
          ),
        },
        {
          heading: "Why depression at 80",
          body: (
            <div className="space-y-5">
              <p>
                The losses are profound and continuous. A spouse of fifty years.
                Siblings. Nearly all the friends who knew you when you were
                young. Independence, as driving stops and the house becomes
                harder to manage. Physical health, with its long list of
                conditions and the long list of medications for them. And
                loneliness, which at 80 can be nearly total, and which is one of
                the most powerful drivers of depression at any age.
              </p>
            </div>
          ),
        },
        {
          heading: "What treatment looks like",
          body: (
            <div className="space-y-5">
              <p>
                Therapy works at 80. People are often surprised to hear that,
                but older adults engage well with it and benefit.
                Antidepressants can help, chosen with great care because of
                interactions with other medications and heightened sensitivity
                to side effects: dizziness, falls, confusion. A good doctor will
                also check for medical mimics like thyroid problems, vitamin
                deficiencies, and medication effects.
              </p>
              <p>
                Connection matters more than anything. Whatever social contact
                and physical movement you can manage, do it.
              </p>
            </div>
          ),
        },
        {
          heading: "When medication isn't the answer",
          body: (
            <div className="space-y-5">
              <p>
                At 80, medication is more likely to cause problems and less
                likely to fully work. If two trials haven't brought relief, or
                the side effects have been intolerable, you have
                treatment-resistant depression, and adding more pills is often
                the wrong direction.
              </p>
              <p>
                There's another way. Depression isn't only a chemistry problem;
                it's a circuitry problem. The region behind the forehead that
                regulates mood is underactive. Treatments that stimulate that
                region directly don't go through the bloodstream at all, which
                at 80 is exactly what you want.
              </p>
            </div>
          ),
        },
        {
          heading: "A gentle, non-drug option",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an FDA-cleared, accelerated form of transcranial magnetic
                stimulation, developed at Stanford, for adults whose depression
                hasn't responded adequately to medication. A 45-minute MRI of
                your own brain identifies the precise target. Treatment is ten
                short 10-minute sessions a day, with rest breaks between them,
                for five consecutive days. You're awake, seated, and you go home
                each evening. No anesthesia, no sedation, no drug interactions.
                Your medications continue.
              </p>
              <p>
                Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> Our clinical team reviews your
                  full medical and psychiatric history and confirms you're a
                  candidate, including a careful safety screening. The initial
                  consultation is a Zoom call; as steps evolve, you'll also have
                  the option to meet by Zoom web conference or in person. The
                  mandatory in-person steps include fMRI mapping and the actual
                  treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days.
                </li>
              </ol>

              <SaintComparisonStatBand />

              <p>
                In clinical trials of people who had failed medication, 79
                percent reached remission, with an average time to remission of
                2.6 days. Across more than 400 trial patients, 85 percent
                experienced symptom relief, with no serious adverse events. The
                most common side effects were mild headache, fatigue, and
                temporary scalp discomfort.
              </p>
              <p>
                SAINT isn't appropriate for people with certain metal implants
                near the head or implanted devices such as pacemakers. That
                screening is done carefully at consultation, along with a review
                of your overall health, and it's the reason a consultation is
                the first step rather than a decision.
              </p>
            </div>
          ),
        },
        {
          heading: "For families",
          body: (
            <div className="space-y-5">
              <p>
                If your parent or grandparent is 80 and has been diagnosed with
                depression, the most important thing you can do is refuse to let
                it be dismissed as age. Get them to the appointments. Sit in on
                them if they'll have you. Ask about non-drug options if
                medication isn't working or isn't safe. And remember that
                treating their depression is not just about mood. It affects
                their heart, their memory, their mobility, and how many good
                years they have left.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                At Neuro Wellness TMS Centers of America, a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>
                , the first appointment is an evaluation, not a commitment. We
                offer private outpatient care with a dedicated care team, and
                family members are welcome at every step. We'll review the full
                medical and psychiatric picture, screen carefully for safety,
                and tell you honestly whether SAINT is appropriate. Medicare and
                other coverage varies; our team can help you understand your
                options.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you or a loved one are in crisis or thinking about self-harm,
                call or text 988 (Suicide &amp; Crisis Lifeline) or go to your
                nearest emergency room. This article is for educational purposes
                and is not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
